import request from "@/utils/request";
import { downloadByXhr } from "@/utils/download";

/** 游戏多语言项 */
export interface GameLangItem {
  language: string;
  code: string;
  name: string;
}

/** 游戏项定义 */
export interface GameItem {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  /** 游戏编号 */
  gameNumber: number;
  /** 游戏名称 */
  name: string;
  /** 描述 */
  desc: string;
  /** 服务器状态 (1: 正常) */
  serverStatus: number;
  rtpId: number;
  /** 最大中奖 */
  maxWin: number;
  betLevelId: string;
  /** RTP (百分比) */
  rtp: string;
  /** 游戏封面 */
  imgUrl: string;
  /** 游戏图标 */
  iconUrl: string;
  /** 横屏游戏介绍视频地址 */
  horizontalVideoUrl?: string;
  /** 竖屏游戏介绍视频地址 */
  verticalVideoUrl?: string;
  /** 游戏地址 */
  gameUrl: string;
  /** 是否关闭 (0: 否, 1: 是) */
  closed: number;
  /** 上线时间 */
  onlineTime: number;
  /** 是否对商户开放 */
  openToOperator: number;
  /** 游戏类型 (1: Slot等) */
  gameType: number;
  /** 波动性 */
  volatile: number;
  /** 玩法/线数 */
  ways: number;
  /** 是否有免费旋转 (0: 否, 1: 是) */
  freeSpinGame: number;
  maxWinBuyBonusType: string;
  activeBuyBonusType: string;
  buyFreespinBuyBonusType: string;
  /** 卷轴配置 */
  reelConfig: string;
  /** 赔付线 */
  payline: number;
  /** 投注范围开始 */
  betRangeBegin: number;
  /** 投注范围结束 */
  betRangeEnd: number;
  /** 中奖率 */
  winRate: number;
  /** 状态 (0: 正常, 1: 禁用) */
  status: number;
  /** 创建时间戳 */
  createdAtTimestamp: number;
  /** 多语言名称 */
  languageNames?: string;
  /** 游戏标签ID集合（逗号分隔字符串，如: 1,2,3） */
  tagIds?: string | null;
  /** RTP调权开关：0关闭 1开启 */
  rtpAdjust?: number;
}

/** 获取游戏列表参数 */
export interface GetGameListParams {
  page?: string;
  size?: string;
  startTime?: string;
  endTime?: string;
  gameType?: string;
  keyword?: string;
  /** 仅查询已开通的游戏 */
  onlyOpen?: boolean;
}

/** 创建/更新游戏请求参数 */
export interface CreateUpdateGameParams {
  id?: number;
  gameNumber?: number;
  imgUrl?: string;
  gameName?: string;
  gameType?: number;
  volatile?: number;
  rtp?: string;
  iconUrl?: string;
  /** 横屏游戏介绍视频地址 */
  horizontalVideoUrl?: string;
  /** 竖屏游戏介绍视频地址 */
  verticalVideoUrl?: string;
  onlineTime?: number;
  reelConfig?: string;
  payline?: number;
  betRangeBegin?: number;
  betRangeEnd?: number;
  freeSpinGame?: number;
  languageNames?: string;
  winRate?: number;
  status?: number;
  /** 游戏标签ID集合（逗号分隔字符串，如: 1,2,3） */
  tagIds?: string;
  /** RTP调权开关：0关闭 1开启 */
  rtpAdjust?: number;
  /** 开通商户 appid 列表（逗号分隔） */
  openAppids?: string;
  /** 谷歌验证成功后获得的票据 */
  verifyToken?: string;
}

/** 游戏商户项定义 */
export interface GameMerchantItem {
  id: string;
  parentOperator: string;
  name: string;
  contactPerson: string;
  contactWay: string;
  status: number; // 0: 关闭, 1: 开启
}

/** 游戏类型定义 */
export interface GameTypeItem {
  id: number;
  name: string;
  sort: number;
  createdAt: number;
  updatedAt: number;
  gameCount?: number; // 该类型下的游戏数量
}

/** 游戏标签定义 */
export interface GameTagItem {
  id: number;
  name: string;
  sort: number;
  createdAt: number;
  updatedAt: number;
}

/** 对象存储节点定义 */
export interface ObjectStorageItem {
  name: string;
  key: string;
  isDir: boolean;
  children?: ObjectStorageItem[];
}

export interface ObjectStorageTreeResponse {
  root: ObjectStorageItem;
}

export interface DownloadProgressInfo {
  percent: number;
  loaded: number;
  total: number;
  lengthComputable: boolean;
}

/** 获取游戏列表 */
export async function getGameList(params: GetGameListParams) {
  return request<{ list: GameItem[]; total: number }>("/api/v1/sys/listGame", {
    method: "GET",
    params,
  });
}

/** 获取游戏类型列表 */
export async function getGameTypeList() {
  return request<{ list: GameTypeItem[] }>("/api/v1/sys/listGameType", {
    method: "GET",
  });
}

/** 获取游戏标签列表 */
export async function listGameTag() {
  return request<{ list: GameTagItem[] } | GameTagItem[]>(
    "/api/v1/sys/listGameTag",
    {
      method: "GET",
    },
  );
}

/** 创建游戏标签 */
export async function createGameTag(data: { name: string; sort: number }) {
  return request("/api/v1/sys/createGameTag", {
    method: "POST",
    data,
  });
}

/** 更新游戏标签 */
export async function updateGameTag(data: {
  id: number;
  name: string;
  sort: number;
}) {
  return request("/api/v1/sys/updateGameTag", {
    method: "POST",
    data,
  });
}

/** 删除游戏标签 */
export async function deleteGameTag(id: number) {
  return request("/api/v1/sys/deleteGameTag", {
    method: "POST",
    data: { id },
  });
}

/** 创建游戏类型 */
export async function createGameType(data: { name: string; sort: number }) {
  return request("/api/v1/sys/createGameType", {
    method: "POST",
    data,
  });
}

/** 更新游戏类型 */
export async function updateGameType(data: {
  id: number;
  name: string;
  sort: number;
}) {
  return request("/api/v1/sys/updateGameType", {
    method: "POST",
    data,
  });
}

/** 删除游戏类型 */
export async function deleteGameType(id: number) {
  return request("/api/v1/sys/deleteGameType", {
    method: "POST",
    data: { id },
  });
}

/** 获取游戏关联商户列表 */
export async function getGameMerchantList(params: {
  gameId: number;
  merchantId?: string;
  parentId?: string;
  page?: string;
  size?: string;
}) {
  return request<{ list: GameMerchantItem[]; total: number }>(
    "/api/v1/sys/listGameMerchant",
    {
      method: "GET",
      params,
    },
  );
}

/** 更新游戏商户状态 */
export async function updateGameMerchantStatus(data: {
  gameId: number;
  merchantIds: string[];
  status: number;
}) {
  return request("/api/v1/sys/updateGameMerchantStatus", {
    method: "POST",
    data,
  });
}

/** 创建游戏 */
export async function createGame(data: CreateUpdateGameParams) {
  return request("/api/v1/sys/createGame", {
    method: "POST",
    data,
  });
}

/** 更新游戏 (全量或部分更新) */
export async function updateGame(data: CreateUpdateGameParams) {
  return request("/api/v1/sys/updateGame", {
    method: "POST",
    data,
  });
}

/** 修改游戏状态 (支持批量) */
export async function updateGameStatus(data: {
  ids: string;
  status: number;
  verifyToken: string;
}) {
  return request("/api/v1/sys/updateGameStatus", {
    method: "POST",
    data,
  });
}

/** 获取对象存储列表（树结构） */
export async function listObjectStorage() {
  // 本地开发优先读取 public/assets.json；非开发环境仍走后端接口
  // if (process.env.NODE_ENV === "development") {
  //   const response = await fetch("/assets.json");
  //   if (!response.ok) {
  //     throw new Error("failed to load /assets.json");
  //   }
  //   return (await response.json()) as ObjectStorageTreeResponse;
  // }

  return request<ObjectStorageTreeResponse>("/api/v1/sys/listObjectStorage", {
    method: "GET",
  });
}

/** 下载对象存储文件 */
export async function downloadObjectStorage(
  key: string,
  onProgress?: (progress: DownloadProgressInfo) => void,
) {
  const result = await downloadByXhr({
    url: "/api/v1/sys/downloadObjectStorage",
    method: "GET",
    params: { key },
    onDownloadProgress: onProgress
      ? ({ loaded, total, lengthComputable }) => {
          const percent =
            lengthComputable && total > 0
              ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100)))
              : 0;
          onProgress({ percent, loaded, total, lengthComputable });
        }
      : undefined,
  });

  if (onProgress) {
    const loaded = result.data?.size || 0;
    onProgress({
      percent: 100,
      loaded,
      total: loaded,
      lengthComputable: true,
    });
  }

  return result.data;
}
